import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";

import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";

import { parseISO } from "date-fns/parseISO";

import ConfirmationDialog from "lib/components/dialog/ConfirmationDialog";

import { asTitle, flipElement } from "utils/Utils";
import DatePicker from "utils/elements/DatePicker";

import {
	InformationRight,
	ReportRight,
	ReportRights
} from "core/blueprint/trajectory/TrajectoryBlueprintService";
import CreateReportDialog from "../core/report/CreateReportDialog";

import {
	ReportInfo,
	deleteTrajectoryReport,
	getTrajectoryReports
} from "trajectory/TrajectoryService";

import ReportOverviewTable from "./report/ReportOverviewTable";

export interface ReportOverviewProps {
	trajectoryId: number;
	rights: ReportRights;
	reportBlueprints: string[];

	onCreate: (reportId: number) => void;
}

const ReportOverview: React.FC<ReportOverviewProps> = (
	props: ReportOverviewProps
) => {
	const { t } = useTranslation();

	const [selected, setSelected] = useState<number[]>([]);
	const [reports, setReports] = useState<ReportInfo[]>();

	const [startDate, setStartDate] = useState<Date | null>(null);
	const [typeFilter, setTypeFilter] = useState("");

	const [createDialog, setCreateDialog] = useState(false);
	const [deleteDialog, setDeleteDialog] = useState(false);

	useEffect(() => {
		loadReports();
	}, [props.trajectoryId]);

	function loadReports(): Promise<void> {
		return getTrajectoryReports(props.trajectoryId).then(data =>
			setReports(data)
		);
	}

	async function deleteReports(): Promise<void> {
		for (const reportId of selected) {
			await deleteTrajectoryReport(props.trajectoryId, reportId);
		}

		loadReports()
			.then(() => setSelected([]))
			.finally(() => setDeleteDialog(false));
	}

	const confirmDeleteReports = useCallback((): void => {
		setDeleteDialog(true);
	}, []);

	function matchesFilter(report: ReportInfo): boolean {
		const matchesStartDate =
			startDate === null || parseISO(report.created) < startDate;
		const matchesType = typeFilter === "" || report.name === typeFilter;

		return matchesStartDate && matchesType;
	}

	const updateTypeFilter = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>): void => {
			setTypeFilter(event.target.value);
		},
		[]
	);

	return (
		<>
			{Object.keys(props.rights).length === 0 && (
				<Alert severity="warning">
					{t("noAccess", { section: "report_other" })}
				</Alert>
			)}

			{Object.keys(props.rights).length > 0 && (
				<>
					<Grid
						container
						columnSpacing={2}
						rowSpacing={2}
						direction={"row-reverse"}
						justifyContent={"space-between"}>
						<Grid
							item
							container
							xs={12}
							sm={8}
							md={5}
							columnSpacing={2}
							sx={{ paddingBottom: 2 }}>
							<Grid item xs={6}>
								<DatePicker
									label={asTitle(t("changesStartDate"))}
									disableFuture
									inputProps={{
										variant: "standard",
										fullWidth: true
									}}
									value={startDate}
									onChange={(date: Date | null): void => {
										if (date !== null) {
											date.setHours(0);
											date.setMinutes(0);
											date.setSeconds(0);
											date.setMilliseconds(0);
										}
										setStartDate(date);
									}}
								/>
							</Grid>
							<Grid item xs={6}>
								<TextField
									label={asTitle(t("type"))}
									value={typeFilter}
									onChange={updateTypeFilter}
									variant="standard"
									fullWidth
									select>
									<MenuItem value="">
										<p style={{ margin: 12 }} />
									</MenuItem>
									{props.reportBlueprints.map(blueprint => (
										<MenuItem
											key={blueprint}
											value={blueprint}>
											{blueprint}
										</MenuItem>
									))}
								</TextField>
							</Grid>
						</Grid>

						<Grid
							item
							container
							alignItems="center"
							xs={12}
							sm={6}
							md={4}>
							<Grid item>
								<Button
									variant="outlined"
									disabled={
										!props.rights[""]?.[""]?.[
											ReportRight.ADMIN
										] &&
										!props.reportBlueprints.some(
											blueprint =>
												props.rights[blueprint]?.[""]?.[
													ReportRight.ADMIN
												]
										)
									}
									startIcon={<AddIcon />}
									onClick={() => setCreateDialog(true)}>
									{asTitle(t("createReport"))}
								</Button>
							</Grid>
							<Grid>
								<Tooltip title={t("delete") ?? ""}>
									<span>
										<IconButton
											id="delete-report-button"
											aria-label="delete-report"
											disabled={selected.length === 0}
											onClick={confirmDeleteReports}
											size="large">
											<DeleteIcon />
										</IconButton>
									</span>
								</Tooltip>
							</Grid>
						</Grid>
					</Grid>

					<ReportOverviewTable
						reports={
							reports
								?.sort((a, b) =>
									b.created.localeCompare(a.created)
								)
								.filter(report => matchesFilter(report))
								.filter(report =>
									Object.entries(
										props.rights[report.name]
									).some(entry =>
										entry[0] === ""
											? entry[1]?.[ReportRight.READ] ===
											  true
											: entry[1]?.[
													InformationRight.READ
											  ] === true
									)
								) ?? []
						}
						rights={props.rights}
						selected={selected}
						onSelect={(id, s) =>
							setSelected(prevState => flipElement(id, prevState))
						}
					/>
				</>
			)}

			{createDialog && (
				<CreateReportDialog
					trajectoryId={props.trajectoryId}
					reportBlueprints={props.reportBlueprints.filter(
						blueprint =>
							props.rights[""]?.[""]?.[ReportRight.ADMIN] ===
								true ||
							props.rights[blueprint]?.[""]?.[
								ReportRight.ADMIN
							] === true
					)}
					onClose={() => setCreateDialog(false)}
				/>
			)}

			{deleteDialog && (
				<ConfirmationDialog
					title={t("deleteDocumentTitle", {
						document: "report",
						count: selected.length
					})}
					dialogText={t("deleteDocumentMessage", {
						document: "report",
						count: selected.length
					})}
					cancelLabel={t("cancel")}
					confirmLabel={t("delete")}
					onClose={() => setDeleteDialog(false)}
					onConfirm={deleteReports}
				/>
			)}
		</>
	);
};

export default ReportOverview;
